import { isVAOnHelpCenterGAEnabled } from '@helpcenter/feature-flags';
import { getMeta } from '@helpcenter/meta';
import ViewHomepageQuery from 'view/advanced-help-center/view-homepage/__generated__/viewHomepageQuery.graphql';
import type { viewHomepageQuery } from 'view/advanced-help-center/view-homepage/__generated__/viewHomepageQuery.graphql';
import type { viewHomepageForCsmQuery } from 'view/customer-service/view-homepage/__generated__/viewHomepageForCsmQuery.graphql';
import ViewHomepageForCsmQuery from 'view/customer-service/view-homepage/__generated__/viewHomepageForCsmQuery.graphql';
import type { searchLoaderVirtualAgentEnabledQuery as searchLoaderVirtualAgentEnabledQueryV1 } from 'view/search/__generated__/searchLoaderVirtualAgentEnabledQuery.graphql';
import SearchLoaderVirtualAgentEnabledQueryV1 from 'view/search/__generated__/searchLoaderVirtualAgentEnabledQuery.graphql';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import { isCSMHelpCenter } from '@atlassian/help-center-common-util/advanced-help-center';
import { getEnv } from '@atlassian/help-center-common-util/env';
import { createRelayResource } from '@atlassian/help-center-common-util/resource-router/resource-router';
import KnowledgeCardsSearchQuery from '../../../customer-service/knowledge-cards/src/ui/__generated__/mainKnowledgeCardsSearchArticlesForSupportSiteQuery.graphql';
import type { mainKnowledgeCardsSearchArticlesForSupportSiteQuery } from '../../../customer-service/knowledge-cards/src/ui/__generated__/mainKnowledgeCardsSearchArticlesForSupportSiteQuery.graphql';
import type { searchLoaderVirtualAgentEnabledV2Query } from '../../../help-center/search/src/__generated__/searchLoaderVirtualAgentEnabledV2Query.graphql';
import SearchLoaderVirtualAgentEnabledV2Query from '../../../help-center/search/src/__generated__/searchLoaderVirtualAgentEnabledV2Query.graphql';

export const ViewHomepageResource = isCSMHelpCenter(getEnv().helpCenterType)
    ? createRelayResource<viewHomepageForCsmQuery>({
          type: 'VIEW_HOMEPAGE',
          getQuery: (_, { cloudId, helpCenterAri, layoutAri }) => {
              return {
                  parameters: ViewHomepageForCsmQuery,
                  variables: {
                      cloudId,
                      helpCenterAri,
                      layoutAri,
                      filter: { isEditMode: false },
                  },
                  options: {
                      fetchPolicy: 'store-or-network',
                  },
              };
          },
      })
    : createRelayResource<viewHomepageQuery>({
          type: 'VIEW_HOMEPAGE',
          getQuery: (_, { cloudId, helpCenterAri, layoutAri }) => {
              return {
                  parameters: ViewHomepageQuery,
                  variables: {
                      cloudId,
                      helpCenterAri,
                      layoutAri,
                      filter: { isEditMode: false },
                  },
                  options: {
                      fetchPolicy: 'store-or-network',
                  },
              };
          },
      });

export const searchComponentVirtualAgentResource = fg('helpcenter_search_api_changes')
    ? createRelayResource<searchLoaderVirtualAgentEnabledV2Query>({
          type: 'HELP_CENTER_SEARCH_VIRTUAL_AGENT',
          getQuery: (_, { cloudId, helpCenterAri, isUserLoggedIn }) => {
              const isLoggedIn = FeatureGates.checkGate('jsd-search-router-login-logic-removal')
                  ? Boolean(getMeta('ajs-atlassian-account-id'))
                  : isUserLoggedIn;
              return {
                  parameters: SearchLoaderVirtualAgentEnabledV2Query,
                  variables: {
                      cloudId,
                      helpCenterAri,
                      isVaOnHcGAEnabled: isVAOnHelpCenterGAEnabled() && isLoggedIn,
                  },
                  options: {
                      fetchPolicy: 'store-or-network',
                  },
              };
          },
      })
    : createRelayResource<searchLoaderVirtualAgentEnabledQueryV1>({
          type: 'HELP_CENTER_SEARCH_VIRTUAL_AGENT',
          getQuery: (_, { cloudId, helpCenterAri, isUserLoggedIn }) => {
              const isLoggedIn = FeatureGates.checkGate('jsd-search-router-login-logic-removal')
                  ? Boolean(getMeta('ajs-atlassian-account-id'))
                  : isUserLoggedIn;
              return {
                  parameters: SearchLoaderVirtualAgentEnabledQueryV1,
                  variables: {
                      cloudId,
                      helpCenterAri,
                      isVaOnHcGAEnabled: isVAOnHelpCenterGAEnabled() && isLoggedIn,
                  },
                  options: {
                      fetchPolicy: 'store-or-network',
                  },
              };
          },
      });

const MAX_ITEMS_PER_PAGE = 9;
const SELECTED_CONTENT_TYPE = 'FOLDER';

export const knowledgeCardsResource = createRelayResource<mainKnowledgeCardsSearchArticlesForSupportSiteQuery>({
    type: 'KNOWLEDGE_CARDS',
    getQuery: (_, { cloudId, helpCenterAri }) => {
        return {
            parameters: KnowledgeCardsSearchQuery,
            variables: {
                cloudId,
                helpCenterAri,
                first: MAX_ITEMS_PER_PAGE,
                input: {
                    contentTypes: [SELECTED_CONTENT_TYPE],
                    topLevelArticlesOnly: true,
                },
            },
            options: {
                fetchPolicy: 'store-or-network',
            },
        };
    },
});
